import React, { ReactElement } from 'react';
import { useTranslation } from './useTranslation';

interface TransProps {
  i18nKey: string;
  components?: Record<string, ReactElement>;
  values?: Record<string, string | number>;
}

/**
 * Custom Trans component for interpolating React components into translations
 * 
 * Works with the existing window.reactAppProps.texts translation system.
 * No need for i18next initialization or manual typing of translation keys.
 * 
 * @example
 * Translation string: "I agree to the <termsLink>Terms</termsLink> and <privacyLink>Privacy Policy</privacyLink>"
 * 
 * Usage:
 * <Trans
 *   i18nKey="auth.tos_agree"
 *   components={{
 *     termsLink: <Button onClick={...} />,
 *     privacyLink: <Button onClick={...} />
 *   }}
 * />
 */
export const Trans: React.FC<TransProps> = ({ i18nKey, components = {}, values = {} }) => {
  const { t } = useTranslation();
  
  // Get the translated string
  let translatedString = t(i18nKey, values);
  
  // Parse the string and build React elements
  const elements = parseTranslation(translatedString, components);
  
  return <>{elements}</>;
};

/**
 * Parse translation string and replace component placeholders with actual React components
 * 
 * @param text - Translation string with component placeholders like "<componentName>text</componentName>"
 * @param components - Map of component names to React elements
 * @returns Array of React elements and strings
 */
function parseTranslation(text: string, components: Record<string, ReactElement>): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  
  // Regex to match component tags: <componentName>content</componentName>
  const regex = /<(\w+)>(.*?)<\/\1>/g;
  
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  
  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push(text.substring(lastIndex, match.index));
    }
    
    const componentName = match[1];
    const content = match[2];
    const component = components[componentName];
    
    if (component) {
      // Clone the component and add the content as children
      elements.push(
        React.cloneElement(component, { key: match.index, children: content })
      );
    } else {
      // If component not found, just add the raw text
      console.warn(`Trans: Component "${componentName}" not found in components prop`);
      elements.push(match[0]);
    }
    
    lastIndex = regex.lastIndex;
  }
  
  // Add remaining text after last match
  if (lastIndex < text.length) {
    elements.push(text.substring(lastIndex));
  }
  
  return elements;
}
